<div class="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#09090b] dark:text-slate-100 flex flex-col antialiased selection:bg-emerald-600 selection:text-white transition-colors duration-200">
  <header class="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur px-4 py-3 flex items-center justify-between shadow-xs">
    <div class="flex items-center space-x-2">
      <span class="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse"></span>
      <span class="text-xs font-bold tracking-wider uppercase text-slate-700 dark:text-slate-300">
        League of Managers
      </span>
    </div>

    <div class="relative">
      <!-- Settings Wheel Menu Button -->
      <button
        type="button"
        (click)="toggleSettingsMenu()"
        id="btn-login-settings"
        [title]="game.t('nav.settings')"
        class="flex items-center space-x-1.5 p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer border border-slate-200 dark:border-slate-700/60"
      >
        <app-icon name="settings" [size]="16" />
      </button>

      @if (isSettingsMenuOpen()) {
        <!-- Backdrop click overlay -->
        <div (click)="closeSettingsMenu()" class="fixed inset-0 z-40"></div>

        <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl py-2 z-50 text-xs transition-colors">
          <div class="px-3 py-1.5 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
            {{ game.t('nav.settings') }}
          </div>

          <div class="p-1 space-y-0.5">
            <!-- Modo Oscuro / Claro -->
            <button
              type="button"
              (click)="game.toggleDarkMode()"
              id="login-menu-theme"
              class="w-full flex items-center justify-between px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
            >
              <div class="flex items-center space-x-2.5">
                @if (game.isDarkMode()) {
                  <app-icon name="sun" [size]="16" class="text-amber-400" />
                  <span class="font-medium">{{ game.t('nav.toggle_theme_light') }}</span>
                } @else {
                  <app-icon name="moon" [size]="16" class="text-slate-600 dark:text-slate-300" />
                  <span class="font-medium">{{ game.t('nav.toggle_theme_dark') }}</span>
                }
              </div>
              <div class="w-8 h-4.5 rounded-full p-0.5 transition-colors" [ngClass]="game.isDarkMode() ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'">
                <div class="w-3.5 h-3.5 rounded-full bg-white transition-transform" [ngClass]="game.isDarkMode() ? 'translate-x-3.5' : 'translate-x-0'"></div>
              </div>
            </button>

            <!-- Cambiar Idioma -->
            <div class="pt-1 border-t border-slate-100 dark:border-slate-800">
              <div class="px-3 py-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center justify-between">
                <span class="flex items-center space-x-1.5">
                  <app-icon name="globe" [size]="12" />
                  <span>{{ game.t('nav.language') }}</span>
                </span>
                <span class="font-mono text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{{ game.i18n.currentLanguageOption().shortCode }}</span>
              </div>

              <div class="grid grid-cols-2 gap-1 px-1.5 py-1">
                @for (lang of game.i18n.availableLanguages; track lang.code) {
                  <button
                    type="button"
                    (click)="selectLanguage(lang.code)"
                    [id]="'login-menu-lang-' + lang.code"
                    class="flex items-center space-x-1.5 px-2 py-1.5 rounded-lg text-left cursor-pointer transition-colors"
                    [ngClass]="game.i18n.currentLanguage() === lang.code ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-200 dark:border-emerald-800/60' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'"
                  >
                    <span class="text-sm leading-none">{{ lang.flag }}</span>
                    <span class="text-[11px] truncate">{{ lang.nativeName }}</span>
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </header>

  <main class="flex-1 flex items-center justify-center p-4">
    <div class="w-full max-w-md space-y-6">
      <!-- Brand Header -->
      <div class="text-center space-y-3">
        <img
          src="/icons/lom-192.png"
          alt="LOM — League of Managers"
          width="96"
          height="96"
          class="inline-block w-24 h-24 rounded-2xl object-contain shadow-md"
        />

        <div>
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            League of Managers
          </h1>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {{ game.t('auth.subtitle') }}
          </p>
        </div>
      </div>

      <!-- Login Card (Angular Material Style) -->
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm dark:shadow-2xl transition-colors">
        <div class="mb-6 pb-4 border-b border-slate-100 dark:border-slate-800/80">
          <h2 class="text-base font-bold text-slate-900 dark:text-white">
            {{ game.t('auth.welcome') }}
          </h2>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{ game.t('auth.enter_credentials') }}
          </p>
        </div>

        @if (googleLoginEnabled) {
          <div class="mb-6 pb-6 border-b border-slate-100 dark:border-slate-800/80">
            <button
              type="button"
              (click)="handleGoogleLogin()"
              id="btn-google-login"
              [disabled]="isGoogleLoading()"
              class="w-full flex items-center justify-center space-x-3 py-3 px-4 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 active:bg-slate-100 dark:active:bg-slate-800 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-98"
            >
              @if (isGoogleLoading()) {
                <app-icon name="loader" [size]="18" class="animate-spin text-emerald-600" />
                <span>{{ game.t('auth.google_checking') }}</span>
              } @else {
                <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.95H1.2v3.15C3.18 21.32 7.23 24 12 24z"/>
                  <path fill="#FBBC05" d="M5.28 14.25c-.25-.72-.39-1.49-.39-2.25s.14-1.53.39-2.25V6.6H1.2C.44 8.15 0 9.89 0 12s.44 3.85 1.2 5.4l4.08-3.15z"/>
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.23 0 3.18 2.68 1.2 6.6l4.08 3.15c.95-2.84 3.6-4.95 6.72-4.95z"/>
                </svg>
                <span>{{ game.t('auth.google_continue') }}</span>
              }
            </button>
          </div>
        }

        @if (unverifiedEmail()) {
          <div class="mb-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-900/60 space-y-3" role="alert">
            <div class="flex items-start space-x-2.5">
              <app-icon name="alert-triangle" [size]="18" class="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div class="text-xs text-amber-900 dark:text-amber-200 leading-relaxed font-medium">
                {{ game.t('auth.err_email_not_verified') }}
              </div>
            </div>

            @if (resendSuccess()) {
              <div class="p-2.5 bg-emerald-100/70 dark:bg-emerald-950/80 rounded-lg text-xs text-emerald-800 dark:text-emerald-300 font-semibold" role="status">
                {{ game.t('auth.resend_sent') }}
              </div>
            }

            <div>
              <button
                type="button"
                id="btn-login-resend"
                (click)="handleResendConfirmation()"
                [disabled]="resendCooldown() > 0 || isResending()"
                class="w-full flex items-center justify-center space-x-2 py-2 px-3 rounded-lg text-xs font-semibold text-amber-900 dark:text-amber-100 bg-amber-200/60 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-900/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (isResending()) {
                  <app-icon name="loader" [size]="14" class="animate-spin text-amber-800 dark:text-amber-200" />
                  <span>{{ game.t('auth.resend_sending') }}</span>
                } @else if (resendCooldown() > 0) {
                  <app-icon name="clock" [size]="14" />
                  <span>{{ game.t('auth.resend_wait', { seconds: resendCooldown() }) }}</span>
                } @else {
                  <app-icon name="refresh-cw" [size]="14" />
                  <span>{{ game.t('auth.resend_btn') }}</span>
                }
              </button>
            </div>
          </div>
        }

        <form [formGroup]="loginForm" (ngSubmit)="handleSubmit()" class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5" for="email-input">
              {{ game.t('auth.email') }}
            </label>
            <div class="relative rounded-xl shadow-xs">
              <input
                id="email-input"
                type="text"
                formControlName="user"
                class="block w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                [placeholder]="game.t('auth.user_placeholder')"
              />
            </div>
            @if (loginForm.get('user')?.touched && loginForm.get('user')?.invalid) {
              <p class="text-[11px] text-rose-500 mt-1">{{ game.t('auth.user_required') }}</p>
            }
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5" for="password-input">
              {{ game.t('auth.password') }}
            </label>
            <div class="relative rounded-xl shadow-xs">
              <input
                id="password-input"
                type="password"
                formControlName="password"
                class="block w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                placeholder="••••••••"
              />
            </div>
            @if (loginForm.get('password')?.touched && loginForm.get('password')?.invalid) {
              <p class="text-[11px] text-rose-500 mt-1">{{ game.t('auth.password_required') }}</p>
            }
          </div>

          <div class="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-1">
            <label class="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                formControlName="rememberMe"
                class="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-emerald-600 focus:ring-emerald-500"
              />
              <span>{{ game.t('auth.remember_me') }}</span>
            </label>
          </div>

          <div class="pt-2">
            <button
              type="submit"
              id="btn-submit-login"
              [disabled]="game.isAuthLoading() || loginForm.invalid"
              class="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-98"
            >
              @if (game.isAuthLoading()) {
                <span>{{ game.t('auth.authenticating') }}</span>
              } @else {
                <span>{{ game.t('auth.submit') }}</span>
                <app-icon name="arrow-right" [size]="16" />
              }
            </button>
          </div>
        </form>

        <div class="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800/80 text-center space-y-2">
          <div>
            <a
              routerLink="/register"
              [fragment]="invitationFragment"
              id="link-to-register"
              class="text-xs font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
            >
              {{ game.t('auth.no_account') }}
            </a>
          </div>
          <p class="text-[11px] text-slate-400">League of Managers · {{ game.i18n.currentLanguageOption().nativeName }}</p>
        </div>
      </div>
    </div>
  </main>

  <footer class="border-t border-slate-200 dark:border-slate-800 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
    <span class="font-mono text-[11px]">{{ game.appVersionTag }}</span>
  </footer>
</div>
